import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSpinner, faStore } from '@fortawesome/free-solid-svg-icons';
import { useCurrentSalonStore } from '../stores/useCurrentSalonStore';
import { useSalonStore } from '../features/salons/store/useSalonStore';
import { SalonWithAccessResponse } from '@api/generated';
import { useNavigate } from 'react-router';

interface SalonSwitcherProps {}

const SalonSwitcher: React.FC<SalonSwitcherProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentSalon, setCurrentSalonId } = useCurrentSalonStore();
  const { salons, loadSalons, loading } = useSalonStore();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeSalon = async () => {
      if (salons.length === 0) {
        await loadSalons();
      }

      // Set first salon as default if none selected
      if (!currentSalon && salons.length > 0) {
        setCurrentSalonId(salons[0].id);
        navigate(`/app/${salons[0].id}`);
      }
    };

    initializeSalon();
  }, [salons.length, currentSalon, setCurrentSalonId, loadSalons]);

  const handleSalonSelect = (salon: SalonWithAccessResponse) => {
    // redirect to /app/[salonId]/
    setCurrentSalonId(salon.id);
    navigate(`/app/${salon.id}`);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2`}>
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
        <span className="text-gray-500">Načítání salonů...</span>
      </div>
    );
  }

  return (
    <div className={`relative`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
      >
        <FontAwesomeIcon icon={faStore} className="text-green-600" />
        <span className="text-sm font-medium text-gray-700">{currentSalon ? currentSalon.salonName : 'Vyberte salon'}</span>
        <FontAwesomeIcon icon={faChevronDown} className={`text-xs text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
            {salons.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">Žádné salony nenalezeny</div>
            ) : (
              <div className="py-1">
                {salons.map(salon => (
                  <button
                    key={salon.id}
                    onClick={() => handleSalonSelect(salon)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      currentSalon?.salonId === salon.id ? 'bg-green-50 border-r-2 border-green-500' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={faStore} className="text-green-600 text-sm" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{salon.name}</div>
                        <div className="text-sm text-gray-500">{salon.city}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalonSwitcher;
